import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
} from '@mui/material';
import { Target } from 'lucide-react';

const COLORS = [
  '#0ea5e9', // blue
  '#10b981', // green
  '#ef4444', // red
  '#f59e42', // orange
  '#a855f7', // purple
  '#fbbf24', // yellow
  '#6366f1', // indigo
  '#14b8a6', // teal
  '#eab308', // gold
  '#f472b6', // pink
  '#737373', // slate
];

const WorkTypeDistribution = ({
  title = "Work Type Distribution",
  data = [],
  loading = false,
}) => {
  // Calculate percentages for the data
  const processedData = React.useMemo(() => {
    if (!data || data.length === 0) return [];

    const total = data.reduce((sum, item) => sum + (item.value || 0), 0);
    return data.map((item, index) => ({
      ...item,
      percentage: total > 0 ? (item.value / total) * 100 : 0,
      color: COLORS[index % COLORS.length]
    }));
  }, [data]);

  return (
    <Card sx={{
      borderRadius: 1,
      border: '0 solid #e2e8f0',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      bgcolor: '#fff',
      height: '100%',
      minHeight: { xs: 280, sm: 320, md: 350 }
    }}>
      <CardContent sx={{
        p: { xs: 1.5, sm: 2, md: 3 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Typography variant="h6" sx={{
          fontWeight: 600,
          color: '#1f1f1f',
          mb: { xs: 1.5, sm: 2, md: 3 },
          fontSize: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          letterSpacing: '-0.025em',
          lineHeight: 1,
          gap: 2
        }}>
          <Target size={16}/>
          {title}
        </Typography>

        {loading ? (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            bgcolor: '#f8fafc',
            borderRadius: { xs: 1, sm: 2 },
            minHeight: { xs: 120, sm: 150, md: 200 }
          }}>
            <Typography color="#737373" sx={{
              fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
              textAlign: 'center'
            }}>
              Loading chart data...
            </Typography>
          </Box>
        ) : processedData.length > 0 ? (
          <Box sx={{
            width: '100%',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1.5, sm: 2, md: 2.5 },
            overflowY: 'auto',
            maxHeight: { xs: 250, sm: 300, md: 400 }
          }}>
            {processedData
              .filter(item => item.percentage > 0)
              .map((item, index) => (
                <Box
                  key={`work-type-${index}`}
                  sx={{
                    borderLeft: { xs: `3px solid ${item.color}`, sm: `4px solid ${item.color}` },
                    pl: { xs: 1.5, sm: 2 },
                    py: { xs: 0.5, sm: 1 },
                    width: '98%',
                    borderRadius: 1,
                    bgcolor: 'rgba(248, 250, 252, 0.3)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(248, 250, 252, 0.6)',
                      transform: 'translateX(2px)'
                    }
                  }}
                >
                  {/* Header with name and percentage */}
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: { xs: 0.5, sm: 1 },
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    gap: { xs: 0.5, sm: 0 }
                  }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: { xs: 1, sm: 2 },
                      minWidth: 0,
                      flex: 1
                    }}>
                      <Box sx={{
                        width: { xs: 6, sm: 8 },
                        height: { xs: 6, sm: 8 },
                        borderRadius: '50%',
                        bgcolor: item.color,
                        flexShrink: 0
                      }} />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: '#1f1f1f',
                          lineHeight: 1,
                          fontSize: '0.75rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {item.name}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: '#1f1f1f',
                        lineHeight: '1.25rem',
                        fontSize: '0.875rem',
                        flexShrink: 0,
                        minWidth: 'fit-content'
                      }}
                    >
                      {item.percentage.toFixed(0)}%
                    </Typography>
                  </Box>

                {/* Progress bar */}
                <Box sx={{
                  position: 'relative',
                  bgcolor: '#f1f5f9',
                  borderRadius: { xs: 0.5, sm: 1 },
                  height: { xs: 4, sm: 6 },
                  overflow: 'hidden',
                  mb: { xs: 0.5, sm: 1 },
                  width: '100%'
                }}>
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: `${item.percentage}%`,
                    bgcolor: item.color,
                    borderRadius: { xs: 0.5, sm: 1 },
                    transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                    minWidth: item.percentage > 0 ? '2px' : '0'
                  }} />
                </Box>

                {/* Subtitle */}
                <Typography
                  variant="caption"
                  sx={{
                    color: '#737373',
                    fontSize: { xs: '0.625rem', sm: '0.75rem', md: '0.875rem' },
                    fontWeight: 500,
                    display: 'block'
                  }}
                >
                  Work Percentage
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            bgcolor: '#f8fafc',
            borderRadius: { xs: 1, sm: 2 },
            minHeight: { xs: 120, sm: 150, md: 200 }
          }}>
            <Typography
              color="#737373"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                textAlign: 'center',
                px: { xs: 1, sm: 2 }
              }}
            >
              No work type data available
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default WorkTypeDistribution;